import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Map as MapIcon, Images, Heart, PenLine, Upload, Sparkles, Plane } from "lucide-react";
import { motion } from "framer-motion";
import LetterModal from "@/components/LetterModal";
import UploadModal from "@/components/UploadModal";
import { travelPhotos } from "@/data/photos";

// 恋爱开始日期：2022年10月5日 -> 到2026年5月5日正好是1308天
const LOVE_START_DATE = new Date("2022-10-05T00:00:00");

const nextTripCities = [
  { name: "桂林", desc: "乘竹筏漂流漓江，看山水甲天下的诗意画卷" },
  { name: "兰州", desc: "吃一碗正宗牛肉面，沿着黄河散步看日落" },
  { name: "成都", desc: "在宽窄巷子喝茶，和大熊猫来一场治愈之旅" },
  { name: "昆明", desc: "漫步滇池边，在春城的花海里许下愿望" },
  { name: "重庆", desc: "坐长江索道穿楼而过，吃最辣的火锅看夜景" },
  { name: "西宁", desc: "环青海湖骑行，在茶卡盐湖拍天空之镜" },
  { name: "大理", desc: "住在洱海边的民宿，看苍山雪、洱海月" },
  { name: "杭州", desc: "泛舟西湖断桥边，在龙井村品一杯明前茶" },
];

export default function Home() {
  const [letterOpen, setLetterOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const [loveDuration, setLoveDuration] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [nextTripIndex, setNextTripIndex] = useState(0);
  const heroRef = useRef<HTMLElement>(null);
  const [customPhotos, setCustomPhotos] = useState<typeof travelPhotos>([]);

  // 实时计算恋爱时长
  useEffect(() => {
    const calculateDuration = () => {
      const now = new Date();
      const diff = now.getTime() - LOVE_START_DATE.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setLoveDuration({ days, hours, minutes, seconds });
    };

    calculateDuration();
    const interval = setInterval(calculateDuration, 1000);
    return () => clearInterval(interval);
  }, []);

  // 从 localStorage 读取照片
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        const saved = localStorage.getItem("customPhotos");
        if (saved) {
          setCustomPhotos(JSON.parse(saved));
        }
      }
    } catch {
      setCustomPhotos([]);
    }
  }, []);

  const allPhotos = useMemo(() => [...travelPhotos, ...customPhotos], [customPhotos]);

  // 直接从照片数据中的 city 字段统计城市数量
  const uniqueCities = useMemo(() => {
    return new Set(allPhotos.map((p) => p.city)).size;
  }, [allPhotos]);

  const photosCount = allPhotos.length;

  // 定期刷新照片数据
  useEffect(() => {
    if (typeof window === "undefined") return;

    const loadPhotos = () => {
      try {
        const saved = localStorage.getItem("customPhotos");
        if (saved) setCustomPhotos(JSON.parse(saved));
      } catch { /* ignore */ }
    };

    const interval = setInterval(loadPhotos, 2000);
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "customPhotos") loadPhotos();
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // 桌面端视差滚动效果
  useEffect(() => {
    if (typeof window === "undefined") return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) return;

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setParallaxY(window.scrollY * 0.35);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToContent = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* 日落渐变背景 — 模拟海滩黄昏 */}
        <div
          className="absolute inset-0 hero-parallax-bg"
          style={{
            background: `
              linear-gradient(
                to bottom,
                #2D1B4E 0%,
                #4A2D5E 12%,
                #7B4578 24%,
                #B55B75 36%,
                #D06A60 46%,
                #E8845A 55%,
                #F4A03B 66%,
                #FCE38A 76%,
                #FCE38A 80%,
                #E8A060 88%,
                #C07848 100%
              )
            `,
            transform: `translateY(${parallaxY}px) scale(1.08)`,
            transition: parallaxY === 0 ? "none" : undefined,
          }}
        />

        {/* 太阳光晕 */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse 50% 35% at 50% 68%, rgba(255, 230, 150, 0.45) 0%, transparent 70%),
              radial-gradient(ellipse 30% 20% at 50% 70%, rgba(255, 245, 200, 0.3) 0%, transparent 60%)
            `,
          }}
        />

        {/* 海面反射光柱 */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              linear-gradient(
                to bottom,
                transparent 70%,
                rgba(255, 200, 100, 0.08) 78%,
                rgba(255, 180, 80, 0.12) 82%,
                rgba(255, 160, 60, 0.06) 90%,
                transparent 100%
              )
            `,
            clipPath: "polygon(44% 70%, 56% 70%, 62% 100%, 38% 100%)",
          }}
        />

        {/* 暗角效果 — 聚焦中心 */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse 70% 60% at 50% 50%, transparent 40%, rgba(20, 10, 30, 0.3) 100%)",
          }}
        />

        {/* WCAG 可读性遮罩 — 渐变叠加 */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to bottom, rgba(30, 15, 50, 0.35) 0%, rgba(30, 15, 50, 0.1) 35%, rgba(30, 15, 50, 0.15) 65%, rgba(30, 15, 50, 0.5) 100%)",
          }}
        />

        {/* 浮动粒子效果 */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: 2 + Math.random() * 4,
                height: 2 + Math.random() * 4,
                left: `${5 + Math.random() * 90}%`,
                top: `${20 + Math.random() * 60}%`,
                background: `oklch(${0.7 + Math.random() * 0.2} 0.1 ${55 + Math.random() * 20} / ${0.15 + Math.random() * 0.25})`,
                boxShadow: `0 0 ${4 + Math.random() * 8}px oklch(0.8 0.1 60 / 0.3)`,
              }}
              animate={{
                y: [0, -30 - Math.random() * 50, 0],
                x: [0, (Math.random() - 0.5) * 30, 0],
                opacity: [0.2, 0.6, 0.2],
                scale: [1, 1.3, 1],
              }}
              transition={{
                duration: 4 + Math.random() * 6,
                repeat: Infinity,
                delay: Math.random() * 4,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>

        {/* 微妙颗粒纹理 */}
        <div className="absolute inset-0 grain-overlay pointer-events-none" />

        {/* Hero content */}
        <div className="relative z-10 text-center px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Heart
                className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-5"
                style={{ color: "oklch(0.85 0.14 55)", filter: "drop-shadow(0 0 12px oklch(0.75 0.14 55 / 0.4))" }}
                fill="oklch(0.85 0.14 55 / 0.3)"
              />
            </motion.div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{
              fontFamily: "var(--font-handwritten)",
              fontSize: "clamp(2.5rem, 8vw, 5rem)",
              color: "oklch(0.98 0.01 70)",
              textShadow: "0 2px 30px oklch(0.25 0.08 45 / 0.5), 0 0 60px oklch(0.55 0.15 55 / 0.2)",
              lineHeight: 1.15,
              letterSpacing: "0.02em",
            }}
          >
            我们的旅行足迹
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mx-auto my-4 sm:my-5"
            style={{
              width: 100,
              height: 2,
              background: "linear-gradient(to right, transparent, oklch(0.88 0.12 65 / 0.7), oklch(0.85 0.14 55 / 0.5), oklch(0.88 0.12 65 / 0.7), transparent)",
              borderRadius: 1,
            }}
          />

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mt-2"
            style={{
              fontFamily: "var(--font-family)",
              fontSize: "clamp(1rem, 3vw, 1.4rem)",
              color: "oklch(0.92 0.04 65 / 0.85)",
              fontWeight: 300,
              letterSpacing: "0.15em",
              textShadow: "0 1px 12px oklch(0.25 0.06 45 / 0.3)",
            }}
          >
            和你走过的每一寸时光
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="flex items-center justify-center flex-wrap"
            style={{ gap: "var(--spacing-md)", marginTop: "clamp(2rem, 5vh, 3.5rem)" }}
          >
            <Link
              to="/map"
              className="group flex items-center rounded-full transition-all duration-300 hover:scale-105 active:scale-95"
              style={{
                gap: "var(--spacing-sm)",
                padding: "0.7rem var(--spacing-xl)",
                backgroundColor: "oklch(0.98 0.01 70 / 0.12)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                color: "oklch(0.98 0.01 70)",
                border: "1.5px solid oklch(0.98 0.01 70 / 0.3)",
                fontSize: "var(--font-size-label)",
                fontWeight: 500,
                letterSpacing: "0.05em",
                boxShadow: "0 4px 24px oklch(0.25 0.06 45 / 0.2), inset 0 1px 0 oklch(1 0 0 / 0.1)",
              }}
            >
              <MapIcon className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" />
              旅行地图
            </Link>
            <Link
              to="/timeline"
              className="group flex items-center rounded-full transition-all duration-300 hover:scale-105 active:scale-95"
              style={{
                gap: "var(--spacing-sm)",
                padding: "0.7rem var(--spacing-xl)",
                background: "linear-gradient(135deg, oklch(0.62 0.18 35 / 0.9) 0%, oklch(0.58 0.2 30 / 0.9) 100%)",
                backdropFilter: "blur(8px)",
                color: "oklch(0.98 0 0)",
                border: "1.5px solid oklch(0.65 0.2 35 / 0.5)",
                fontSize: "var(--font-size-label)",
                fontWeight: 500,
                letterSpacing: "0.05em",
                boxShadow: "0 4px 24px oklch(0.55 0.15 35 / 0.35), inset 0 1px 0 oklch(1 0 0 / 0.15)",
              }}
            >
              <Images className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" />
              时光相册
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          onClick={scrollToContent}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center cursor-pointer group"
          style={{ color: "oklch(0.92 0.04 65 / 0.6)" }}
        >
          <span
            className="transition-colors group-hover:text-white"
            style={{ fontSize: "var(--font-size-small)", marginBottom: "var(--spacing-xs)", letterSpacing: "0.1em" }}
          >
            向下滚动
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            className="transition-colors group-hover:text-white"
          >
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.button>
      </section>

      {/* Stats Section */}
      <section className="relative" style={{ padding: "var(--spacing-3xl) var(--spacing-lg)" }}>
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4"
            style={{ gap: "var(--spacing-lg)" }}
          >
            {/* 解锁城市 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 * 0.1 }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className="group flex flex-col items-center rounded-2xl text-center cursor-default"
              style={{
                padding: "var(--spacing-xl)",
                backgroundColor: "oklch(1 0 0)",
                boxShadow: "var(--ds-shadow-md)",
                gap: "var(--spacing-sm)",
                border: "1px solid oklch(0.92 0.02 65 / 0.5)",
                transition: "box-shadow 0.3s ease, border-color 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 12px 32px oklch(0.55 0.08 55 / 0.12), 0 4px 12px oklch(0.55 0.08 55 / 0.06)";
                e.currentTarget.style.borderColor = "oklch(0.62 0.18 35 / 0.25)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "var(--ds-shadow-md)";
                e.currentTarget.style.borderColor = "oklch(0.92 0.02 65 / 0.5)";
              }}
            >
              <div
                className="flex items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
                style={{
                  width: 48,
                  height: 48,
                  backgroundColor: "oklch(0.62 0.18 35 / 0.08)",
                }}
              >
                <MapIcon className="w-5 h-5" style={{ color: "oklch(0.62 0.18 35)" }} />
              </div>
              <span
                className="font-bold"
                style={{
                  fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
                  color: "var(--foreground)",
                  fontFamily: "var(--font-family)",
                  letterSpacing: "-0.02em",
                }}
              >
                {uniqueCities}个
              </span>
              <span style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)" }}>
                解锁城市
              </span>
            </motion.div>

            {/* 下一站旅行 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 1 * 0.1 }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className="group flex flex-col items-center rounded-2xl text-center cursor-pointer"
              onClick={() => {
                const randomIndex = Math.floor(Math.random() * nextTripCities.length);
                setNextTripIndex(randomIndex);
              }}
              style={{
                padding: "var(--spacing-xl)",
                background: "linear-gradient(135deg, oklch(0.96 0.03 220) 0%, oklch(0.93 0.05 240 / 0.7) 100%)",
                boxShadow: "var(--ds-shadow-md)",
                gap: "var(--spacing-xs)",
                border: "1px solid oklch(0.8 0.06 230 / 0.4)",
                position: "relative",
                overflow: "hidden",
                transition: "box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 12px 32px oklch(0.5 0.1 240 / 0.15), 0 4px 12px oklch(0.5 0.1 240 / 0.08)";
                e.currentTarget.style.borderColor = "oklch(0.5 0.12 240 / 0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "var(--ds-shadow-md)";
                e.currentTarget.style.borderColor = "oklch(0.8 0.06 230 / 0.4)";
              }}
            >
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <motion.div
                  className="absolute"
                  animate={{ x: ["-10%", "110%"] }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  style={{ top: "30%", opacity: 0.06 }}
                >
                  <Plane className="w-10 h-10" style={{ color: "oklch(0.5 0.15 240)" }} />
                </motion.div>
              </div>
              <div className="relative z-10 flex items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110" style={{ width: 48, height: 48, backgroundColor: "oklch(0.5 0.15 240 / 0.08)" }}>
                <Sparkles className="w-5 h-5" style={{ color: "oklch(0.5 0.15 240)" }} />
              </div>
              <span className="relative z-10" style={{ fontSize: "var(--font-size-small)", color: "oklch(0.5 0.1 230)", fontFamily: "var(--font-handwritten)", letterSpacing: "0.05em" }}>
                下一站旅行
              </span>
              <span className="font-bold relative z-10" style={{ fontSize: "clamp(1.3rem, 3.5vw, 2rem)", color: "oklch(0.45 0.15 240)", fontFamily: "var(--font-family)", letterSpacing: "-0.02em", textShadow: "oklch(0.55 0.15 240 / 0.15) 0 2px 8px" }}>
                {nextTripCities[nextTripIndex].name}
              </span>
              <span className="relative z-10" style={{ fontSize: "var(--font-size-small)", color: "oklch(0.55 0.08 230)", fontFamily: "var(--font-family)", maxWidth: "90%", lineHeight: 1.4, minHeight: "2.8em" }}>
                {nextTripCities[nextTripIndex].desc}
              </span>
              <div className="relative z-10 flex items-center" style={{ gap: 4, marginTop: "var(--spacing-xs)", padding: "2px 10px", borderRadius: 20, backgroundColor: "oklch(1 0 0 / 0.5)", border: "1px solid oklch(0.8 0.06 230 / 0.3)" }}>
                <Plane className="w-3 h-3" style={{ color: "oklch(0.5 0.12 240)" }} />
                <span style={{ fontSize: "var(--font-size-small)", color: "oklch(0.5 0.1 240)", fontFamily: "var(--font-family)" }}>
                  点击切换 {nextTripIndex + 1}/{nextTripCities.length}
                </span>
              </div>
            </motion.div>

            {/* 恋爱计时器 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 2 * 0.1 }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className="group flex flex-col items-center rounded-2xl text-center"
              style={{
                padding: "var(--spacing-xl)",
                background: "linear-gradient(135deg, oklch(0.97 0.02 45) 0%, oklch(0.95 0.04 55 / 0.6) 100%)",
                boxShadow: "var(--ds-shadow-md)",
                gap: "var(--spacing-xs)",
                border: "1px solid oklch(0.85 0.08 45 / 0.4)",
                position: "relative",
                overflow: "hidden",
                transition: "box-shadow 0.3s ease, border-color 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 12px 32px oklch(0.62 0.12 45 / 0.15), 0 4px 12px oklch(0.62 0.12 45 / 0.08)";
                e.currentTarget.style.borderColor = "oklch(0.65 0.14 45 / 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "var(--ds-shadow-md)";
                e.currentTarget.style.borderColor = "oklch(0.85 0.08 45 / 0.4)";
              }}
            >
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute"
                    initial={{ bottom: "10%", opacity: 0 }}
                    animate={{
                      bottom: ["10%", "120%"],
                      opacity: [0, 0.1 + i * 0.05, 0],
                      x: [0, -20 + Math.sin(i) * 30, 0, 20 + Math.cos(i) * 20, 0],
                      scale: [0.6, 1 + i * 0.15, 0.8],
                    }}
                    transition={{
                      duration: 6 + i * 0.8,
                      repeat: Infinity,
                      delay: i * 0.7,
                      ease: "linear",
                    }}
                    style={{ left: `${15 + i * 18}%` }}
                  >
                    <Heart className="w-3 h-3" fill={`oklch(0.65 0.18 35 / ${0.1 + i * 0.05})`} style={{ color: `oklch(0.65 0.18 35 / ${0.15 + i * 0.05})` }} />
                  </motion.div>
                ))}
              </div>
              <div className="relative z-10 flex items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110" style={{ width: 48, height: 48, backgroundColor: "oklch(0.62 0.18 35 / 0.08)" }}>
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Heart className="w-5 h-5" fill="oklch(0.62 0.18 35 / 0.3)" style={{ color: "oklch(0.62 0.18 35)" }} />
                </motion.div>
              </div>
              <span className="relative z-10" style={{ fontSize: "var(--font-size-small)", color: "oklch(0.55 0.12 45)", fontFamily: "var(--font-handwritten)", letterSpacing: "0.05em" }}>
                Today is our
              </span>
              <div className="relative z-10 flex items-baseline" style={{ gap: "var(--spacing-xs)", lineHeight: 1 }}>
                <motion.span
                  className="font-bold"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                  style={{
                    fontSize: "clamp(2.5rem, 8vw, 3.5rem)",
                    color: "oklch(0.55 0.18 35)",
                    fontFamily: "var(--font-family)",
                    letterSpacing: "-0.03em",
                    textShadow: "oklch(0.65 0.18 35 / 0.2) 0 2px 10px",
                  }}
                >
                  {loveDuration.days}
                </motion.span>
                <span style={{ fontSize: "clamp(1rem, 2.5vw, 1.3rem)", color: "oklch(0.55 0.12 45)", fontFamily: "var(--font-family)", fontWeight: 500 }}>
                  天
                </span>
              </div>
              <span className="relative z-10" style={{ fontSize: "var(--font-size-small)", color: "oklch(0.6 0.1 45)", fontFamily: "var(--font-handwritten)", letterSpacing: "0.03em", marginTop: "var(--spacing-xs)" }}>
                恋爱纪念日
              </span>
              <div className="relative z-10 flex items-center" style={{ gap: "var(--spacing-xs)", marginTop: "var(--spacing-xs)", padding: "2px 10px", borderRadius: 20, backgroundColor: "oklch(1 0 0 / 0.5)", border: "1px solid oklch(0.85 0.06 45 / 0.3)" }}>
                <div>
                  <Heart className="w-3 h-3" fill="oklch(0.62 0.18 35)" style={{ color: "oklch(0.62 0.18 35)" }} />
                </div>
                <span style={{ fontSize: "var(--font-size-small)", color: "oklch(0.5 0.08 45)", fontFamily: "var(--font-family)", fontVariantNumeric: "tabular-nums", letterSpacing: "0.02em" }}>
                  {String(loveDuration.hours).padStart(2, "0")}:{String(loveDuration.minutes).padStart(2, "0")}:{String(loveDuration.seconds).padStart(2, "0")}
                </span>
              </div>
            </motion.div>

            {/* 珍藏照片 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 3 * 0.1 }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className="group flex flex-col items-center rounded-2xl text-center cursor-default"
              style={{
                padding: "var(--spacing-xl)",
                backgroundColor: "oklch(1 0 0)",
                boxShadow: "var(--ds-shadow-md)",
                gap: "var(--spacing-sm)",
                border: "1px solid oklch(0.92 0.02 65 / 0.5)",
                transition: "box-shadow 0.3s ease, border-color 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 12px 32px oklch(0.55 0.08 55 / 0.12), 0 4px 12px oklch(0.55 0.08 55 / 0.06)";
                e.currentTarget.style.borderColor = "oklch(0.62 0.18 35 / 0.25)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "var(--ds-shadow-md)";
                e.currentTarget.style.borderColor = "oklch(0.92 0.02 65 / 0.5)";
              }}
            >
              <div
                className="flex items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
                style={{
                  width: 48,
                  height: 48,
                  backgroundColor: "oklch(0.62 0.18 35 / 0.08)",
                }}
              >
                <Images className="w-5 h-5" style={{ color: "oklch(0.62 0.18 35)" }} />
              </div>
              <span
                className="font-bold"
                style={{
                  fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
                  color: "var(--foreground)",
                  fontFamily: "var(--font-family)",
                  letterSpacing: "-0.02em",
                }}
              >
                {photosCount}张
              </span>
              <span style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)" }}>
                珍藏照片
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Actions */}
      <section style={{ padding: "0 var(--spacing-lg) var(--spacing-3xl)" }}>
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center"
            style={{ gap: "var(--spacing-md)" }}
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setLetterOpen(true)}
              className="group flex items-center rounded-xl transition-all duration-300"
              style={{
                gap: "var(--spacing-sm)",
                padding: "var(--spacing-sm) var(--spacing-lg)",
                backgroundColor: "oklch(0.75 0.14 55 / 0.08)",
                color: "oklch(0.58 0.15 55)",
                border: "1.5px solid oklch(0.75 0.14 55 / 0.18)",
                fontSize: "var(--font-size-label)",
                fontWeight: 500,
                boxShadow: "0 2px 8px oklch(0.55 0.08 55 / 0.04)",
              }}
            >
              <PenLine className="w-4 h-4 transition-transform duration-300 group-hover:rotate-[-12deg]" />
              写一封信
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setUploadOpen(true)}
              className="group flex items-center rounded-xl transition-all duration-300"
              style={{
                gap: "var(--spacing-sm)",
                padding: "var(--spacing-sm) var(--spacing-lg)",
                backgroundColor: "oklch(0.62 0.18 35 / 0.08)",
                color: "oklch(0.52 0.16 35)",
                border: "1.5px solid oklch(0.62 0.18 35 / 0.18)",
                fontSize: "var(--font-size-label)",
                fontWeight: 500,
                boxShadow: "0 2px 8px oklch(0.55 0.08 55 / 0.04)",
              }}
            >
              <Upload className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
              上传照片
            </motion.button>
          </motion.div>
        </div>
      </section>

      <LetterModal open={letterOpen} onClose={() => setLetterOpen(false)} />
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}
